import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Download, Trash2, Wand2, X } from "lucide-react";
import {
  backfillContactNames,
  createContact,
  deleteContact,
  exportContacts,
  liftContactObjection,
  listContactTags,
  setContactObjection,
  setContactTags,
  updateContact,
  updateContactStatusFields,
} from "@/lib/contacts.functions";
import { listContactsComplete } from "@/lib/contacts-bulk.functions";
import { listClients } from "@/lib/customers.functions";
import { describeContactFilters as describeFilters } from "@/lib/contacts";
import { ContactsBulkBar } from "@/components/admin/contacts/ContactsBulkBar";
import { InlineBundeslandSelect } from "@/components/admin/contacts/InlineBundeslandSelect";

import { InlineTagSelect } from "@/components/admin/contacts/InlineTagSelect";

import {
  addContactOrigin,
  listContactSources,
  removeContactOrigin,
} from "@/lib/contact-sources.functions";
import { selectableSources, type ContactSource } from "@/lib/contact-sources";
import { confirmDialog } from "@/lib/confirm-dialog";
import {
  CONTACT_LEGAL_BASES,
  CONTACT_LEGAL_BASIS_LABELS,
  CONTACT_LEGAL_BASIS_OPEN,
  CONTACT_LEGAL_BASIS_OPEN_LABEL,
  contactLegalBasisLabel,
  CONTACT_CLIENT_CATEGORY_LABELS,
  CONTACT_STATUS_LABELS,
  CONTACT_STATUS_OVERRIDES,
  CONTACT_STATUS_OVERRIDE_LABELS,
  BUNDESLAENDER,
  BUNDESLAND_LABELS,
  BUNDESLAND_UNKNOWN,
  BUNDESLAND_UNKNOWN_LABEL,
  effectiveContactStatus,
  contactDisplayName,
  dedupeTagLabels,
  filterContacts,
  formatOrigin,
  sortContacts,
  type ContactSortDirection,
  type ContactSortKey,
  normalizeTagLabel,
  type ContactFilters,
  type ContactLegalBasis,
  type ContactRow,
  type ContactStatus,
  type ContactStatusOverride,
  type Bundesland,
} from "@/lib/contacts";

import { MANDATE_TYPES, MANDATE_TYPE_LABELS, type MandateType } from "@/lib/client-mandate";
import { useAdminSession } from "@/hooks/use-admin-session";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminCard } from "@/components/admin/AdminCard";
import { AdminListToolbar } from "@/components/admin/AdminListToolbar";
import { AdminTableShell } from "@/components/admin/AdminTableShell";
import { AdminSkeletonList } from "@/components/admin/AdminSkeletonList";
import { AdminErrorNotice } from "@/components/admin/AdminErrorNotice";
import { AdminEmptyState } from "@/components/admin/AdminEmptyState";
import { AdminField, adminInputClass } from "@/components/admin/AdminField";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_admin/admin/contacts")({
  component: ContactsPage,
  head: () => ({ meta: [{ title: "Kontaktliste – Interner Bereich" }] }),
});

const dateFmt = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });

function formatDate(value: string | null): string {
  if (!value) return "–";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "–" : dateFmt.format(d);
}

const selectClass = adminInputClass + " pr-8";

/**
 * Herkunft in der Listenansicht: nur das kompakte Label, ohne Details wie
 * Kundenname, Lead-Magnet-Titel oder MOCO-Firmenname. Details bleiben in der
 * Detailansicht des Kontakts sichtbar.
 */
function originLabel(origin: ContactRow["origins"][number]): string {
  return origin.sourceName;
}

function ContactsPage() {
  const { isAdmin } = useAdminSession();
  const queryClient = useQueryClient();

  // Ergänzung 2: Die Liste lädt den Bestand vollständig und sagt es ausdrücklich.
  const listFn = useServerFn(listContactsComplete);
  const q = useQuery({
    queryKey: ["admin", "contacts"],
    queryFn: () => listFn(),
    staleTime: 60_000,
  });

  const tagsFn = useServerFn(listContactTags);
  const tagsQuery = useQuery({
    queryKey: ["admin", "contact-tags"],
    queryFn: () => tagsFn(),
    staleTime: 60_000,
  });

  // Ticket KL3: geführte Herkünfte für Filter, Anlage und Einzelpflege.
  const sourcesFn = useServerFn(listContactSources);
  const sourcesQuery = useQuery({
    queryKey: ["admin", "contact-sources"],
    queryFn: () => sourcesFn(),
    staleTime: 60_000,
  });
  const sources = sourcesQuery.data ?? [];

  const [filters, setFilters] = useState<ContactFilters>({});
  // Ticket SK11: Kontakte aus dem Testbetrieb sind standardmäßig ausgeblendet.
  const [showTest, setShowTest] = useState(false);
  // Ticket KL2: seltener gebrauchte Filter bleiben eingeklappt.
  const [moreFilters, setMoreFilters] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  // Ticket KL5: Sortierung im Browser, Nachname zuerst.
  const [sortKey, setSortKey] = useState<ContactSortKey>("lastName");
  const [sortDir, setSortDir] = useState<ContactSortDirection>("asc");
  /** Punkt 4: einzeln abgewählte Zeilen der gefilterten Auswahl. */
  const [deselected, setDeselected] = useState<Set<string>>(new Set());

  const rows = q.data?.rows ?? [];
  const loadComplete = q.data?.complete ?? true;
  const visible = useMemo(
    () =>
      sortContacts(
        filterContacts(rows, filters).filter((r) => showTest || !r.isTest),
        sortKey,
        sortDir,
      ),
    [rows, filters, showTest, sortKey, sortDir],
  );
  const selectedIds = useMemo(
    () => visible.filter((r) => !deselected.has(r.id)).map((r) => r.id),
    [visible, deselected],
  );
  // Kopfzeilen-Kästchen: gefüllt, wenn nichts abgewählt ist; sonst Teilzustand.
  const allSelected = visible.length > 0 && selectedIds.length === visible.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < visible.length;
  const filterLabel = useMemo(() => {
    const names: Record<string, string> = {};
    for (const s of sources) names[s.id] = s.name;
    const base = describeFilters(filters, { sourceNames: names, includeTest: showTest });
    return deselected.size > 0 ? `${base} · ${deselected.size} Zeilen abgewählt` : base;
  }, [filters, sources, showTest, deselected]);

  const toggleSort = (key: ContactSortKey) => {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const statusFieldsFn = useServerFn(updateContactStatusFields);
  const saveBundesland = async (row: ContactRow, next: Bundesland | null) => {
    await statusFieldsFn({
      data: { id: row.id, bundesland: next, statusOverride: row.statusOverride },
    });
    void queryClient.invalidateQueries({ queryKey: ["admin", "contacts"] });
  };
  const saveStatusOverride = async (row: ContactRow, next: ContactStatusOverride) => {
    await statusFieldsFn({
      data: { id: row.id, bundesland: row.bundesland, statusOverride: next },
    });
    void queryClient.invalidateQueries({ queryKey: ["admin", "contacts"] });
  };
  // Schlagworte aus der Zeile heraus speichern; neue Begriffe legt der Server an.
  const rowTagsFn = useServerFn(setContactTags);
  const saveRowTags = async (row: ContactRow, next: string[]) => {
    try {
      await rowTagsFn({ data: { id: row.id, tags: next } });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Schlagworte nicht gespeichert.");
      throw error;
    }
    void queryClient.invalidateQueries({ queryKey: ["admin", "contacts"] });
    void queryClient.invalidateQueries({ queryKey: ["admin", "contact-tags"] });
  };

  const detail = detailId ? (rows.find((r) => r.id === detailId) ?? null) : null;

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin", "contacts"] });
    void queryClient.invalidateQueries({ queryKey: ["admin", "contact-tags"] });
  };


  const exportFn = useServerFn(exportContacts);
  const exportMutation = useMutation({
    mutationFn: () => exportFn(),
    onSuccess: (result) => {
      const blob = new Blob(["\uFEFF" + result.csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = result.filename;
      a.click();
      URL.revokeObjectURL(url);
    },
    onError: () => toast.error("Ausleiten nicht möglich."),
  });

  const backfillFn = useServerFn(backfillContactNames);
  const backfillMutation = useMutation({
    mutationFn: () => backfillFn(),
    onSuccess: (result) => {
      if (result.candidates === 0) {
        toast.success("Alle Kontakte haben bereits einen Namen.");
        return;
      }
      const filled = result.fromMoco + result.fromOrganization + result.fromEmail;
      toast.success(
        `${filled} von ${result.candidates} Namen ergänzt (Moco: ${result.fromMoco}, Organisation: ${result.fromOrganization}, Adresse: ${result.fromEmail}). Offen: ${result.remaining}.`,
      );
      refresh();
    },
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : "Namen konnten nicht nachgetragen werden."),
  });

  const runBackfill = async () => {
    const ok = await confirmDialog({
      title: "Namen nachtragen",
      description:
        "Für Kontakte ohne Vor- und Nachnamen werden Namen aus Moco, dem Organisationsfeld und der E-Mail-Adresse ergänzt. Bestehende Namen bleiben unverändert.",
      confirmLabel: "Namen nachtragen",
      destructive: false,
    });
    if (ok) backfillMutation.mutate();
  };

  return (
    <div className="max-w-7xl">
      <AdminPageHeader
        title="Kontaktliste"
        description="Ein Kontakt je E-Mail-Adresse, mit allen Herkünften, Rechtsgrundlage und Widerspruchsvermerk. Über diese Liste wird nichts versendet."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={() => setCreateOpen(true)}>
              Kontakt erfassen
            </Button>
            {isAdmin ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => void runBackfill()}
                disabled={backfillMutation.isPending}
              >
                <Wand2 className="mr-2 h-4 w-4" aria-hidden="true" />
                Namen nachtragen
              </Button>
            ) : null}
            {isAdmin ? (
              <Button
                type="button"
                onClick={() => exportMutation.mutate()}
                disabled={exportMutation.isPending}
              >
                <Download className="mr-2 h-4 w-4" aria-hidden="true" />
                Als Datei ausleiten
              </Button>
            ) : null}
          </div>
        }
      />

      <AdminListToolbar
        search={filters.search ?? ""}
        onSearchChange={(value) => setFilters((f) => ({ ...f, search: value }))}
        searchPlaceholder="Name, Adresse, Organisation oder Schlagwort …"
        searchLabel="Kontaktliste durchsuchen"
        count={visible.length}
        total={rows.length}
        filters={
          <div className="w-full space-y-2">
            {/* Ticket KL2: die drei Filter des Alltags stehen vorn. */}
            <div className="grid w-full grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
              <select
                aria-label="Nach Status filtern"
                className={selectClass}
                value={filters.status ?? ""}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, status: (e.target.value || null) as ContactStatus | null }))
                }
              >
                <option value="">Alle Status</option>
                <option value="kunde">{CONTACT_STATUS_LABELS.kunde}</option>
                <option value="interessent">{CONTACT_STATUS_LABELS.interessent}</option>
              </select>
              <select
                aria-label="Nach Herkunft filtern"
                className={selectClass}
                value={filters.sourceId ?? ""}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, sourceId: e.target.value || null }))
                }
              >
                <option value="">Alle Herkünfte</option>
                {sources.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.isActive ? "" : " (deaktiviert)"}
                  </option>
                ))}
              </select>
              <select
                aria-label="Nach Bundesland filtern"
                className={selectClass}
                value={filters.bundesland ?? ""}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    bundesland: (e.target.value || null) as ContactFilters["bundesland"],
                  }))
                }
              >
                <option value="">Alle Bundesländer</option>
                {BUNDESLAENDER.map((k) => (
                  <option key={k} value={k}>
                    {BUNDESLAND_LABELS[k]}
                  </option>
                ))}
                <option value={BUNDESLAND_UNKNOWN}>{BUNDESLAND_UNKNOWN_LABEL}</option>
              </select>
              <Button
                type="button"
                variant="outline"
                className="h-10"
                aria-expanded={moreFilters}
                onClick={() => setMoreFilters((v) => !v)}
              >
                {moreFilters ? "Weitere Filter ausblenden" : "Weitere Filter"}
              </Button>
            </div>

            {moreFilters ? (
              <div className="grid w-full grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                <select
                  aria-label="Nach Schlagwort filtern"
                  className={selectClass}
                  value={filters.tag ?? ""}
                  onChange={(e) => setFilters((f) => ({ ...f, tag: e.target.value || null }))}
                >
                  <option value="">Alle Schlagworte</option>
                  {(tagsQuery.data ?? []).map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="Nach Rechtsgrundlage filtern"
                  className={selectClass}
                  value={filters.legalBasis ?? ""}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      legalBasis: (e.target.value || null) as ContactLegalBasis | "offen" | null,
                    }))
                  }
                >
                  <option value="">Alle Rechtsgrundlagen</option>
                  <option value={CONTACT_LEGAL_BASIS_OPEN}>{CONTACT_LEGAL_BASIS_OPEN_LABEL}</option>
                  {CONTACT_LEGAL_BASES.map((k) => (
                    <option key={k} value={k}>
                      {CONTACT_LEGAL_BASIS_LABELS[k]}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="Nach Widerspruch filtern"
                  className={selectClass}
                  value={filters.objection ?? ""}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, objection: (e.target.value || null) as "ja" | "nein" | null }))
                  }
                >
                  <option value="">Widerspruch: alle</option>
                  <option value="ja">nur mit Widerspruch</option>
                  <option value="nein">nur ohne Widerspruch</option>
                </select>
                <select
                  aria-label="Nach Kundenkategorie filtern"
                  className={selectClass}
                  value={filters.category ?? ""}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      category: (e.target.value || null) as ContactRow["clientCategory"],
                    }))
                  }
                >
                  <option value="">Alle Kategorien</option>
                  {(["agenturkunde", "kurskunde"] as const).map((k) => (
                    <option key={k} value={k}>
                      {CONTACT_CLIENT_CATEGORY_LABELS[k]}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="Nach Mandatstyp filtern"
                  className={selectClass}
                  value={filters.mandateType ?? ""}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, mandateType: (e.target.value || null) as MandateType | null }))
                  }
                >
                  <option value="">Alle Mandatstypen</option>
                  {MANDATE_TYPES.map((k) => (
                    <option key={k} value={k}>
                      {MANDATE_TYPE_LABELS[k]}
                    </option>
                  ))}
                </select>
                <label className="flex h-10 items-center gap-2 rounded-md border border-border bg-background px-3 text-xs font-semibold">
                  <input
                    type="checkbox"
                    checked={showTest}
                    onChange={(e) => setShowTest(e.target.checked)}
                  />
                  Testdaten einblenden
                </label>
              </div>
            ) : null}
          </div>
        }

      />

      {q.isPending ? <AdminSkeletonList /> : null}
      {q.isError ? (
        <AdminErrorNotice
          title="Kontaktliste nicht geladen"
          message="Die Liste konnte nicht abgerufen werden."
          action={
            <Button type="button" variant="outline" onClick={() => void q.refetch()}>
              Erneut versuchen
            </Button>
          }
        />
      ) : null}

      {!q.isPending && !q.isError ? (
        visible.length === 0 ? (
          <AdminEmptyState
            title="Keine Kontakte gefunden"
            description="Passen Sie Suche oder Filter an, oder erfassen Sie einen Kontakt."
          />
        ) : (
          <div className="mt-4">
            <AdminTableShell>
              <table className="w-full min-w-[940px] text-sm">
                <thead>
                  <tr className="text-left">
                    <th className="w-8 px-3 py-2">
                      {/* Alle sichtbaren Zeilen auf einmal an- oder abwählen. */}
                      <input
                        type="checkbox"
                        aria-label="Alle angezeigten Kontakte für Sammelaktionen berücksichtigen"
                        title="Alle angezeigten Kontakte an- oder abwählen"
                        checked={allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected;
                        }}
                        onChange={() =>
                          setDeselected(
                            allSelected ? new Set(visible.map((r) => r.id)) : new Set<string>(),
                          )
                        }
                      />
                    </th>
                    <th className="px-3 py-2">
                      <SortHeader
                        label="Nachname"
                        active={sortKey === "lastName"}
                        direction={sortDir}
                        onClick={() => toggleSort("lastName")}
                      />
                    </th>
                    <th className="px-3 py-2">
                      <SortHeader
                        label="Vorname"
                        active={sortKey === "firstName"}
                        direction={sortDir}
                        onClick={() => toggleSort("firstName")}
                      />
                    </th>
                    <th className="px-3 py-2">
                      <SortHeader
                        label="E-Mail-Adresse"
                        active={sortKey === "email"}
                        direction={sortDir}
                        onClick={() => toggleSort("email")}
                      />
                    </th>
                    
                    <th className="px-3 py-2">Bundesland</th>
                    <th className="px-3 py-2">Herkunft</th>
                    <th className="px-3 py-2">Schlagworte</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((row) => (
                    <tr key={row.id} className="border-t border-border align-middle">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          aria-label={`${contactDisplayName(row)} bei Sammelaktionen berücksichtigen`}
                          checked={!deselected.has(row.id)}
                          onChange={(e) =>
                            setDeselected((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.delete(row.id);
                              else next.add(row.id);
                              return next;
                            })
                          }
                        />
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => setDetailId(row.id)}
                          className="font-display text-sm font-extrabold text-brand-petrol underline-offset-2 hover:underline"
                        >
                          {row.lastName.trim() || "—"}
                        </button>
                        {row.isTest ? (
                          <span className="ml-2 inline-flex items-center rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-semibold text-foreground">
                            Testbetrieb
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-xs">{row.firstName.trim() || "—"}</td>
                      <td className="px-3 py-2 text-xs">{row.email}</td>
                      <td className="px-3 py-2 text-xs">
                        <InlineBundeslandSelect
                          value={row.bundesland}
                          onSave={(next) => saveBundesland(row, next)}
                        />
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {row.origins.length === 0
                          ? "–"
                          : row.origins.map((o) => (
                              // Das Datum bleibt der Detailansicht vorbehalten.
                              <div key={o.id} className="text-xs">
                                {originLabel(o)}
                              </div>
                            ))}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {/* Schlagworte an- und abmarkern, neue direkt anlegen. */}
                        <InlineTagSelect
                          value={row.tags}
                          suggestions={tagsQuery.data ?? []}
                          onSave={(next) => saveRowTags(row, next)}
                        />
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </AdminTableShell>
          </div>
        )
      ) : null}

      {!q.isPending && !q.isError ? (
        <ContactsBulkBar
          ids={selectedIds}
          filterLabel={filterLabel}
          sources={sources}
          isAdmin={isAdmin}
          loadIncomplete={!loadComplete}
          selectionActive={deselected.size > 0}
          onAllSelected={() => setDeselected(new Set())}
          onClearSelection={() => setDeselected(new Set())}
          onDone={refresh}
        />
      ) : null}

      <ContactDetailDialog
        contact={detail}
        isAdmin={isAdmin}
        sources={sources}
        tagSuggestions={tagsQuery.data ?? []}
        onClose={() => setDetailId(null)}
        onChanged={() => {
          setDetailId(null);
          refresh();
        }}
        onRefresh={refresh}
      />


      <CreateContactDialog
        sources={sources}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          refresh();
        }}
      />
    </div>
  );
}

/** Ticket KL5, Punkt 1: sortierbare Spaltenköpfe. */
function SortHeader({
  label,
  active,
  direction,
  onClick,
}: {
  label: string;
  active: boolean;
  direction: ContactSortDirection;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 font-display text-xs font-extrabold text-foreground"
      aria-label={`Nach ${label} sortieren`}
    >
      {label}
      {active ? (
        direction === "asc" ? (
          <ArrowUp className="h-3 w-3" aria-hidden="true" />
        ) : (
          <ArrowDown className="h-3 w-3" aria-hidden="true" />
        )
      ) : null}
    </button>
  );
}

/** Schlagworte pflegen: Marken entfernen, Vorschläge übernehmen, neue eintippen. */
function TagEditor({
  value,
  suggestions,
  disabled,
  onChange,
}: {
  value: string[];
  suggestions: string[];
  disabled: boolean;
  onChange: (tags: string[]) => void;
}) {
  const [draft, setDraft] = useState("");
  const used = new Set(value.map(normalizeTagLabel));
  const open = suggestions.filter((s) => !used.has(normalizeTagLabel(s)));

  const add = (label: string) => {
    const next = dedupeTagLabels([...value, label]);
    if (next.length !== value.length) onChange(next);
    setDraft("");
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {value.length === 0 ? (
          <p className="text-xs text-muted-foreground">Noch keine Schlagworte vergeben.</p>
        ) : (
          value.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 rounded-md bg-brand-petrol/10 px-2 py-1 font-display text-xs font-extrabold text-brand-petrol"
            >
              {tag}
              <button
                type="button"
                aria-label={`Schlagwort ${tag} entfernen`}
                disabled={disabled}
                className="text-brand-petrol/70 hover:text-destructive disabled:opacity-50"
                onClick={() => onChange(value.filter((t) => t !== tag))}
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </span>
          ))
        )}
      </div>

      <div className="flex gap-2">
        <input
          aria-label="Schlagwort hinzufügen"
          className={adminInputClass}
          placeholder="Schlagwort eintippen …"
          value={draft}
          disabled={disabled}
          maxLength={60}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              if (draft.trim()) add(draft);
            }
          }}
        />
        <Button type="button" variant="outline" disabled={disabled || !draft.trim()} onClick={() => add(draft)}>
          Hinzufügen
        </Button>
      </div>

      {open.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          <span className="text-xs text-muted-foreground">Bereits genutzt:</span>
          {open.slice(0, 24).map((s) => (
            <button
              key={s}
              type="button"
              disabled={disabled}
              className="rounded-md border border-border px-2 py-0.5 text-xs hover:bg-muted disabled:opacity-50"
              onClick={() => add(s)}
            >
              {s}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ContactDetailDialog({
  contact,
  isAdmin,
  sources,
  tagSuggestions,
  onClose,
  onChanged,
  onRefresh,
}: {
  contact: ContactRow | null;
  isAdmin: boolean;
  sources: ContactSource[];
  tagSuggestions: string[];
  onClose: () => void;
  onChanged: () => void;
  onRefresh: () => void;
}) {
  const [note, setNote] = useState("");
  const [wording, setWording] = useState("");
  const objectFn = useServerFn(setContactObjection);
  const liftFn = useServerFn(liftContactObjection);

  // Ticket KL2: Bundesland und Festlegung werden lokal gehalten und bewusst gespeichert.
  const [statusFields, setStatusFields] = useState<{
    bundesland: Bundesland | null;
    statusOverride: ContactStatusOverride;
  }>({ bundesland: null, statusOverride: "automatisch" });
  useEffect(() => {
    if (!contact) return;
    setStatusFields({
      bundesland: contact.bundesland,
      statusOverride: contact.statusOverride,
    });
  }, [contact?.id, contact?.bundesland, contact?.statusOverride]);

  const [core, setCore] = useState({ firstName: "", lastName: "", organization: "", email: "" });

  useEffect(() => {
    if (!contact) return;
    setCore({
      firstName: contact.firstName,
      lastName: contact.lastName,
      organization: contact.organization ?? "",
      email: contact.email,
    });
  }, [contact?.id, contact?.firstName, contact?.lastName, contact?.organization, contact?.email]);

  const updateFn = useServerFn(updateContact);
  const updateMutation = useMutation({
    mutationFn: (id: string) =>
      updateFn({
        data: {
          id,
          firstName: core.firstName.trim(),
          lastName: core.lastName.trim(),
          organization: core.organization.trim() || null,
          email: core.email.trim(),
        },
      }),
    onSuccess: () => {
      toast.success("Stammdaten gespeichert.");
      onRefresh();
    },
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : "Stammdaten nicht gespeichert."),
  });

  const statusFieldsFn = useServerFn(updateContactStatusFields);
  const statusMutation = useMutation({
    mutationFn: (id: string) =>
      statusFieldsFn({
        data: {
          id,
          bundesland: statusFields.bundesland,
          statusOverride: statusFields.statusOverride,
        },
      }),
    onSuccess: () => {
      toast.success("Status und Bundesland gespeichert.");
      onRefresh();
    },
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : "Angaben nicht gespeichert."),
  });

  const tagsFn = useServerFn(setContactTags);
  const tagsMutation = useMutation({
    mutationFn: (input: { id: string; tags: string[] }) => tagsFn({ data: input }),
    onSuccess: () => onRefresh(),
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : "Schlagworte nicht gespeichert."),
  });


  const objectMutation = useMutation({
    mutationFn: (id: string) => objectFn({ data: { id, note: note.trim() || null } }),
    onSuccess: () => {
      toast.success("Widerspruch vermerkt. Der Kontakt bleibt bestehen und ist dauerhaft gesperrt.");
      setNote("");
      onChanged();
    },
    onError: () => toast.error("Widerspruch konnte nicht gespeichert werden."),
  });

  const liftMutation = useMutation({
    mutationFn: (id: string) =>
      liftFn({
        data: {
          id,
          legalBasis: "ausdrueckliche_einwilligung" as ContactLegalBasis,
          legalBasisAt: new Date().toISOString(),
          legalBasisWordingVersion: wording.trim() || null,
        },
      }),
    onSuccess: () => {
      toast.success("Neue Einwilligung dokumentiert, Widerspruch aufgehoben.");
      setWording("");
      onChanged();
    },
    onError: () => toast.error("Aufhebung nicht möglich."),
  });

  const deleteFn = useServerFn(deleteContact);
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Kontakt gelöscht.");
      onChanged();
    },
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : "Löschen nicht möglich."),
  });

  const askDelete = async (row: ContactRow) => {
    const ok = await confirmDialog({
      title: "Kontakt löschen?",
      description: `${contactDisplayName(row)} (${row.email}) wird samt aller Herkünfte endgültig entfernt. Ein Widerspruchsvermerk geht damit verloren – bei Widerspruch ist das Sperren die sichere Wahl.`,
      confirmLabel: "Endgültig löschen",
    });
    if (ok) deleteMutation.mutate(row.id);
  };

  return (
    <Dialog open={contact !== null} onOpenChange={(open) => (!open ? onClose() : undefined)}>

      <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto">
        {contact ? (
          <>
            <DialogHeader>
              <DialogTitle>{contactDisplayName(contact)}</DialogTitle>
              <DialogDescription>{contact.email}</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 text-sm">
              <AdminCard title="Stammdaten">
                <div className="space-y-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <AdminField label="Vorname">
                      {(p) => (
                        <input
                          {...p}
                          className={adminInputClass}
                          value={core.firstName}
                          maxLength={120}
                          onChange={(e) => setCore((c) => ({ ...c, firstName: e.target.value }))}
                        />
                      )}
                    </AdminField>
                    <AdminField label="Nachname">
                      {(p) => (
                        <input
                          {...p}
                          className={adminInputClass}
                          value={core.lastName}
                          maxLength={120}
                          onChange={(e) => setCore((c) => ({ ...c, lastName: e.target.value }))}
                        />
                      )}
                    </AdminField>
                  </div>
                  <AdminField label="Organisation">
                    {(p) => (
                      <input
                        {...p}
                        className={adminInputClass}
                        value={core.organization}
                        maxLength={200}
                        onChange={(e) => setCore((c) => ({ ...c, organization: e.target.value }))}
                      />
                    )}
                  </AdminField>
                  <AdminField
                    label="E-Mail-Adresse"
                    hint={
                      isAdmin
                        ? "Eine Adresse erscheint genau einmal; eine bereits vergebene Adresse wird abgewiesen."
                        : "Das Ändern der Adresse ist der Administration vorbehalten."
                    }
                  >
                    {(p) => (
                      <input
                        {...p}
                        type="email"
                        className={adminInputClass}
                        value={core.email}
                        disabled={!isAdmin}
                        onChange={(e) => setCore((c) => ({ ...c, email: e.target.value }))}
                      />
                    )}
                  </AdminField>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={updateMutation.isPending || core.email.trim().length === 0}
                    onClick={() => updateMutation.mutate(contact.id)}
                  >
                    Stammdaten speichern
                  </Button>
                </div>
              </AdminCard>

              <AdminCard title="Status und Bundesland">
                <div className="space-y-3">
                  <p className="text-xs">
                    Abgeleiteter Status:{" "}
                    <strong>{CONTACT_STATUS_LABELS[effectiveContactStatus(contact).derived]}</strong>
                    {" · "}wirksam: <strong>{CONTACT_STATUS_LABELS[effectiveContactStatus(contact).status]}</strong>
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <AdminField label="Bundesland">
                      {(p) => (
                        <select
                          {...p}
                          className={selectClass}
                          value={statusFields.bundesland ?? ""}
                          onChange={(e) =>
                            setStatusFields((v) => ({
                              ...v,
                              bundesland: (e.target.value || null) as Bundesland | null,
                            }))
                          }
                        >
                          <option value="">{BUNDESLAND_UNKNOWN_LABEL}</option>
                          {BUNDESLAENDER.map((k) => (
                            <option key={k} value={k}>
                              {BUNDESLAND_LABELS[k]}
                            </option>
                          ))}
                        </select>
                      )}
                    </AdminField>
                    <AdminField
                      label="Statusfestlegung"
                      hint="„automatisch“ folgt der Ableitung aus Kauf und Kundenakte."
                    >
                      {(p) => (
                        <select
                          {...p}
                          className={selectClass}
                          value={statusFields.statusOverride}
                          onChange={(e) =>
                            setStatusFields((v) => ({
                              ...v,
                              statusOverride: e.target.value as ContactStatusOverride,
                            }))
                          }
                        >
                          {CONTACT_STATUS_OVERRIDES.map((k) => (
                            <option key={k} value={k}>
                              {CONTACT_STATUS_OVERRIDE_LABELS[k]}
                            </option>
                          ))}
                        </select>
                      )}
                    </AdminField>
                  </div>
                  {contact.statusOverride !== "automatisch" ? (
                    <p className="text-xs text-muted-foreground">
                      Festgelegt am {formatDate(contact.statusOverrideAt)}
                      {contact.statusOverrideBy ? ` durch ${contact.statusOverrideBy}` : ""}
                      {contact.statusOverrideSource ? ` · ${contact.statusOverrideSource}` : ""}
                    </p>
                  ) : null}
                  <Button
                    type="button"
                    variant="outline"
                    disabled={statusMutation.isPending}
                    onClick={() => statusMutation.mutate(contact.id)}
                  >
                    Status und Bundesland speichern
                  </Button>
                </div>
              </AdminCard>

              <AdminCard title="Schlagworte">
                <TagEditor
                  value={contact.tags}
                  suggestions={tagSuggestions}
                  disabled={tagsMutation.isPending}
                  onChange={(tags) => tagsMutation.mutate({ id: contact.id, tags })}
                />
              </AdminCard>


              <AdminCard title="Herkünfte">
                <OriginEditor contact={contact} sources={sources} onChanged={onRefresh} />
              </AdminCard>

              <AdminCard title="Rechtsgrundlage">
                <p className="text-xs">
                  {contactLegalBasisLabel(contact.legalBasis)}
                  {contact.legalBasisAt ? ` · ${formatDate(contact.legalBasisAt)}` : ""}
                  {contact.legalBasisWordingVersion
                    ? ` · Wortlaut ${contact.legalBasisWordingVersion}`
                    : ""}
                </p>
              </AdminCard>

              <AdminCard title="Widerspruch">
                {contact.objectedAt ? (
                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-destructive">
                      Widersprochen am {formatDate(contact.objectedAt)} – dauerhaft von jedem
                      werblichen Versand ausgeschlossen.
                    </p>
                    {contact.objectionNote ? (
                      <p className="text-xs text-muted-foreground">{contact.objectionNote}</p>
                    ) : null}
                    {isAdmin ? (
                      <div className="space-y-2">
                        <AdminField
                          label="Neue, dokumentierte Einwilligung (Wortlaut-Version)"
                          hint="Nur damit ist der Widerspruch aufhebbar."
                        >
                          {(p) => (
                            <input
                              {...p}
                              className={adminInputClass}
                              value={wording}
                              onChange={(e) => setWording(e.target.value)}
                            />
                          )}
                        </AdminField>
                        <Button
                          type="button"
                          variant="outline"
                          disabled={liftMutation.isPending || wording.trim().length === 0}
                          onClick={() => liftMutation.mutate(contact.id)}
                        >
                          Widerspruch mit neuer Einwilligung aufheben
                        </Button>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <AdminField label="Vermerk zum Widerspruch" hint="Optional, z. B. Weg des Eingangs.">
                      {(p) => (
                        <input
                          {...p}
                          className={adminInputClass}
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                        />
                      )}
                    </AdminField>
                    <Button
                      type="button"
                      variant="destructive"
                      disabled={objectMutation.isPending}
                      onClick={() => objectMutation.mutate(contact.id)}
                    >
                      Widerspruch vermerken
                    </Button>
                  </div>
                )}
              </AdminCard>
            </div>

            <DialogFooter className="sm:justify-between">
              {isAdmin ? (
                <Button
                  type="button"
                  variant="destructive"
                  disabled={deleteMutation.isPending}
                  onClick={() => void askDelete(contact)}
                >
                  <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                  Kontakt löschen
                </Button>
              ) : (
                <span />
              )}
              <Button type="button" variant="outline" onClick={onClose}>
                Schließen
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Ticket KL3: Herkünfte am Kontakt pflegen. Auswahl ausschließlich aus den
 * aktiven Herkünften; bestehende Vermerke bleiben auch dann sichtbar, wenn ihre
 * Herkunft inzwischen deaktiviert ist.
 */
function OriginEditor({
  contact,
  sources,
  onChanged,
}: {
  contact: ContactRow;
  sources: ContactSource[];
  onChanged: () => void;
}) {
  const active = useMemo(() => selectableSources(sources), [sources]);
  const [sourceId, setSourceId] = useState("");
  const [leadMagnet, setLeadMagnet] = useState("");

  const addFn = useServerFn(addContactOrigin);
  const removeFn = useServerFn(removeContactOrigin);

  const chosen = active.find((s) => s.id === sourceId) ?? null;
  const needsLeadMagnet = chosen?.systemKey === "lead_magnet";

  const addMutation = useMutation({
    mutationFn: () =>
      addFn({
        data: {
          contactId: contact.id,
          sourceId,
          leadMagnet: needsLeadMagnet ? leadMagnet.trim() || null : null,
        },
      }),
    onSuccess: () => {
      toast.success("Herkunft ergänzt.");
      setSourceId("");
      setLeadMagnet("");
      onChanged();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Herkunft nicht ergänzt."),
  });

  const removeMutation = useMutation({
    mutationFn: (originId: string) => removeFn({ data: { originId } }),
    onSuccess: () => {
      toast.success("Herkunft entfernt.");
      onChanged();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Herkunft nicht entfernt."),
  });

  return (
    <div className="space-y-3">
      <ul className="space-y-1 text-xs">
        {contact.origins.map((o) => (
          <li key={o.id} className="flex items-start justify-between gap-2">
            <span>
              {formatOrigin(o)}{" "}
              <span className="text-muted-foreground">({formatDate(o.occurredAt)})</span>
              {o.addedManually ? (
                <span className="ml-1 text-[10px] font-semibold text-accent">von Hand</span>
              ) : null}
              {o.sourceActive ? null : (
                <span className="ml-1 text-[10px] text-muted-foreground">deaktiviert</span>
              )}
            </span>
            <button
              type="button"
              aria-label={`Herkunft ${o.sourceName} entfernen`}
              disabled={removeMutation.isPending}
              className="text-brand-petrol/70 hover:text-destructive disabled:opacity-50"
              onClick={async () => {
                const ok = await confirmDialog({
                  title: "Herkunft entfernen?",
                  description: `„${formatOrigin(o)}" wird bei diesem Kontakt entfernt. Die Herkunft selbst bleibt bestehen.`,
                  confirmLabel: "Entfernen",
                });
                if (ok) removeMutation.mutate(o.id);
              }}
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </li>
        ))}
        {contact.origins.length === 0 ? <li>Keine Herkunft vermerkt.</li> : null}
      </ul>

      <div className="flex flex-wrap items-end gap-2">
        <select
          aria-label="Herkunft hinzufügen"
          className={selectClass}
          value={sourceId}
          onChange={(e) => setSourceId(e.target.value)}
        >
          <option value="">Herkunft wählen …</option>
          {active.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        {needsLeadMagnet ? (
          <input
            aria-label="Welcher Lead-Magnet?"
            className={adminInputClass}
            placeholder="Bezeichnung des Lead-Magneten"
            value={leadMagnet}
            onChange={(e) => setLeadMagnet(e.target.value)}
          />
        ) : null}
        <Button
          type="button"
          variant="outline"
          disabled={
            addMutation.isPending || !sourceId || (needsLeadMagnet && leadMagnet.trim().length === 0)
          }
          onClick={() => addMutation.mutate()}
        >
          Hinzufügen
        </Button>
      </div>
    </div>
  );
}

function CreateContactDialog({
  sources,
  open,
  onClose,
  onCreated,
}: {
  sources: ContactSource[];
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const active = selectableSources(sources);
  const [form, setForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    organization: "",
    legalBasis: "ausdrueckliche_einwilligung" as ContactLegalBasis,
    wording: "",
    sourceId: "",
    leadMagnet: "",
    // Ticket DM1: Handanlage kann einer Person direkt einen Kunden zuordnen.
    clientId: "",
  });

  const chosen = active.find((s) => s.id === form.sourceId) ?? null;

  const clientsFn = useServerFn(listClients);
  const clientsQuery = useQuery({
    queryKey: ["admin", "contact-create-clients"],
    queryFn: () => clientsFn({}),
    enabled: open,
  });

  const createFn = useServerFn(createContact);
  const mutation = useMutation({
    mutationFn: () =>
      createFn({
        data: {
          email: form.email.trim(),
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          organization: form.organization.trim() || null,
          clientId: form.clientId || null,
          legalBasis: form.legalBasis,
          legalBasisWordingVersion: form.wording.trim() || null,
          sourceId: form.sourceId,
          leadMagnet: chosen?.systemKey === "lead_magnet" ? form.leadMagnet.trim() || null : null,
        },
      }),
    onSuccess: (result) => {
      toast.success(
        (result as { merged?: boolean }).merged
          ? "Adresse war bereits vorhanden – Herkunft ergänzt."
          : "Kontakt angelegt.",
      );
      onCreated();
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Anlegen nicht möglich."),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Kontakt erfassen</DialogTitle>
          <DialogDescription>
            Eine bekannte Adresse wird nicht doppelt angelegt; es wird lediglich die Herkunft ergänzt.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          <AdminField label="E-Mail-Adresse" required>
            {(p) => (
              <input
                {...p}
                type="email"
                className={adminInputClass}
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              />
            )}
          </AdminField>
          <AdminField label="Organisation">
            {(p) => (
              <input
                {...p}
                className={adminInputClass}
                value={form.organization}
                onChange={(e) => setForm((f) => ({ ...f, organization: e.target.value }))}
              />
            )}
          </AdminField>
          <AdminField label="Vorname">
            {(p) => (
              <input
                {...p}
                className={adminInputClass}
                value={form.firstName}
                onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
              />
            )}
          </AdminField>
          <AdminField label="Nachname">
            {(p) => (
              <input
                {...p}
                className={adminInputClass}
                value={form.lastName}
                onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
              />
            )}
          </AdminField>
          <AdminField label="Herkunft" required>
            {(p) => (
              <select
                {...p}
                className={selectClass}
                value={form.sourceId}
                onChange={(e) => setForm((f) => ({ ...f, sourceId: e.target.value }))}
              >
                <option value="">Herkunft wählen …</option>
                {active.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            )}
          </AdminField>
          <AdminField label="Kunde (freiwillig)">
            {(p) => (
              <select
                {...p}
                className={selectClass}
                value={form.clientId}
                onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value }))}
              >
                <option value="">Kein Kunde</option>
                {(clientsQuery.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.organization_name}
                  </option>
                ))}
              </select>
            )}
          </AdminField>
          {chosen?.systemKey === "lead_magnet" ? (
            <AdminField label="Welcher Lead-Magnet?" required>
              {(p) => (
                <input
                  {...p}
                  className={adminInputClass}
                  value={form.leadMagnet}
                  onChange={(e) => setForm((f) => ({ ...f, leadMagnet: e.target.value }))}
                />
              )}
            </AdminField>
          ) : null}
          <AdminField label="Rechtsgrundlage" required>
            {(p) => (
              <select
                {...p}
                className={selectClass}
                value={form.legalBasis}
                onChange={(e) =>
                  setForm((f) => ({ ...f, legalBasis: e.target.value as ContactLegalBasis }))
                }
              >
                {CONTACT_LEGAL_BASES.map((k) => (
                  <option key={k} value={k}>
                    {CONTACT_LEGAL_BASIS_LABELS[k]}
                  </option>
                ))}
              </select>
            )}
          </AdminField>
          <AdminField label="Wortlaut-Version" hint="Sofern vorhanden.">
            {(p) => (
              <input
                {...p}
                className={adminInputClass}
                value={form.wording}
                onChange={(e) => setForm((f) => ({ ...f, wording: e.target.value }))}
              />
            )}
          </AdminField>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            type="button"
            disabled={
              mutation.isPending || form.email.trim().length === 0 || form.sourceId.length === 0
            }
            onClick={() => mutation.mutate()}
          >
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
